// src/composables/useCookieConsent.js
// Shared, reactive cookie-consent state persisted in localStorage.
// Only two categories exist on this site:
//  - necessary: always on (the consent choice itself, stored locally)
//  - externalMedia: Google Maps embed (sets third-party Google cookies)
import { reactive, computed, readonly } from 'vue';

const STORAGE_KEY = 'islaw-cookie-consent';
// Bump this when the policy/categories change so users are asked again.
const CONSENT_VERSION = 1;

function loadStoredConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION || !parsed.categories) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

const stored = loadStoredConsent();

const state = reactive({
  decided: stored !== null,
  externalMedia: stored ? stored.categories.externalMedia === true : false,
  bannerVisible: stored === null,
  preferencesOpen: false,
});

function persist() {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        version: CONSENT_VERSION,
        timestamp: new Date().toISOString(),
        categories: {
          necessary: true,
          externalMedia: state.externalMedia,
        },
      })
    );
  } catch (e) {
    // Storage unavailable (private mode, etc.) — consent stays session-only.
  }
}

function applyChoice(externalMedia) {
  state.externalMedia = externalMedia;
  state.decided = true;
  state.bannerVisible = false;
  state.preferencesOpen = false;
  persist();
}

export function useCookieConsent() {
  return {
    consent: readonly(state),
    allowExternalMedia: computed(() => state.decided && state.externalMedia),
    acceptAll: () => applyChoice(true),
    rejectNonEssential: () => applyChoice(false),
    savePreferences: (externalMedia) => applyChoice(externalMedia === true),
    // Grant a single category from an in-context prompt (e.g. the map placeholder).
    grantExternalMedia: () => applyChoice(true),
    openPreferences: () => {
      state.preferencesOpen = true;
      state.bannerVisible = true;
    },
    withdrawConsent: () => applyChoice(false),
  };
}
